import React, { useState, useRef, useEffect } from 'react';

const Video = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [hasPlayed, setHasPlayed] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  const videoSrc = "/images/videoo/IMG_6021.webm";
  const videoSubtitle = "Celebrating Excellence and Leadership in Industry";

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (videoRef.current) {
          if (entry.isIntersecting) {
            videoRef.current.muted = isMuted;
            const playPromise = videoRef.current.play();
            
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  setIsPlaying(true);
                  setHasPlayed(true);
                })
                .catch(error => {
                  console.log("Autoplay blocked. Muting to allow playback.");
                  videoRef.current.muted = true;
                  videoRef.current.play();
                });
            }
          } else {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, options);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isMuted]);

  const togglePlay = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.muted = isMuted;
        videoRef.current.play();
        setHasPlayed(true);
      }
      setIsPlaying(!isPlaying);
      showControlsTemporarily();
    }
  };

  const toggleMute = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      showControlsTemporarily();
    }
  };

  const toggleFullscreen = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const showControlsTemporarily = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const handleVideoClick = (e) => {
    togglePlay(e);
  };

  const handleMouseMove = () => {
    showControlsTemporarily();
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowControls(true);
  };

  const PlayIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="rgba(255,255,255,0.9)">
      <path d="M8 5v14l11-7z" />
    </svg>
  );

  const PauseIcon = () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="rgba(255,255,255,0.9)">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
  );

  const MuteIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
      <path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" />
    </svg>
  );

  const VolumeIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
      <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );

  const FullscreenIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );

  return (
    <>
      <style>
        {`
          .video-section {
            background: linear-gradient(135deg, #0a0f1c 0%, #0c1120 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
            padding: 4rem 1rem;
          }

          .video-container {
            max-width: 800px;
            width: 100%;
            margin: 0 auto;
            position: relative;
          }

          .video-header {
            text-align: center;
            margin-bottom: 2.5rem;
          }

          .section-pre-title {
            color: #f3b33d;
            font-size: 0.85rem;
            font-weight: 400;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            margin-bottom: 0.75rem;
            display: block;
            font-family: 'Comfortaa', cursive;
          }

          .video-title {
            color: #ffffff;
            font-size: 3rem;
            font-weight: 400;
            letter-spacing: -0.02em;
            margin-bottom: 0.5rem;
            line-height: 1.1;
            font-family: 'Cormorant Garamond', serif;
          }

          .video-title .accent {
            color: #f3b33d;
            font-weight: 600;
          }

          .video-subtitle {
            color: #9aa7b2;
            font-size: 1.1rem;
            font-weight: 300;
            letter-spacing: 0.05em;
            margin-top: 1rem;
            font-family: 'Comfortaa', cursive;
          }

          .video-wrapper {
            position: relative;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
            transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            background: #000;
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .luxury-video {
            width: 100%;
            height: auto;
            display: block;
            aspect-ratio: 16/9;
            cursor: pointer;
            object-fit: cover;
          }

          .video-controls {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            transition: opacity 0.5s ease, visibility 0.5s;
            opacity: ${showControls ? '1' : '0'};
            visibility: ${showControls ? 'visible' : 'hidden'};
            display: flex;
            gap: 25px;
            align-items: center;
            z-index: 20;
          }

          .control-btn {
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.08);
            border: 1px solid rgba(255, 255, 255, 0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            backdrop-filter: blur(12px);
            padding: 0;
            outline: none;
            box-shadow: 0 4px 15px rgba(0,0,0,0.3);
          }

          .control-btn:hover {
            background: rgba(255, 255, 255, 0.15);
            transform: scale(1.15);
            border-color: rgba(243, 179, 61, 0.5);
            box-shadow: 0 0 20px rgba(243, 179, 61, 0.2);
          }

          .control-btn:active {
            transform: scale(0.95);
          }

          .play-pause-btn { width: 85px; height: 85px; }
          .side-btn { width: 55px; height: 55px; }

          .video-progress {
            position: absolute;
            bottom: 0; left: 0; right: 0; height: 4px;
            background: rgba(255, 255, 255, 0.1);
            z-index: 10;
          }

          .progress-fill {
            height: 100%;
            background: #f3b33d;
            width: 0%;
            box-shadow: 0 0 10px rgba(243, 179, 61, 0.5);
          }

          @media (max-width: 768px) {
            .video-title { font-size: 2.2rem; }
            .video-container { max-width: 95%; }
            .video-controls { gap: 15px; }
            .play-pause-btn { width: 70px; height: 70px; }
            .side-btn { width: 45px; height: 45px; }
          }
        `}
      </style>

      <section ref={containerRef} className="video-section" id="video">
        <div className="video-container">
          <div className="video-header">
            <span className="section-pre-title">Achievement Spotlight</span>
            <h1 className="video-title">
              Ermiyas' <span className="accent">Success</span>
            </h1>
            <div className="video-subtitle">{videoSubtitle}</div>
          </div>
          
          <div 
            className="video-wrapper"
            onMouseMove={handleMouseMove}
            onClick={handleVideoClick}
          >
            <video
              ref={videoRef}
              className="luxury-video"
              src={videoSrc}
              preload="auto"
              playsInline
              muted={isMuted}
              onEnded={handleVideoEnd}
              onTimeUpdate={() => {
                if (videoRef.current) {
                  const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
                  const fill = document.querySelector('.progress-fill');
                  if (fill) fill.style.width = `${progress}%`;
                }
              }}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
            
            <div className="video-controls">
              <button 
                type="button"
                className="control-btn side-btn"
                onClick={toggleMute}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <MuteIcon /> : <VolumeIcon />}
              </button>

              <button 
                type="button"
                className="control-btn play-pause-btn"
                onClick={togglePlay}
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </button>
              
              <button 
                type="button"
                className="control-btn side-btn"
                onClick={toggleFullscreen}
                aria-label="Fullscreen"
              >
                <FullscreenIcon />
              </button>
            </div>
            
            <div className="video-progress">
              <div className="progress-fill"></div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Video;